import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { BackgroundProcessObjectType } from "./backgroundProcessingApi";
import { isFinalStatus, isRunningStatus } from "./backgroundProcessStatus";
import {
  selectProcess,
  subscribeProcess,
  TrackedProcess,
  unsubscribeProcess,
} from "./backgroundProcessingSlice";

export interface UseBackgroundProcess {
  process: TrackedProcess | undefined;
  status: string | undefined;
  isRunning: boolean;
  isFinal: boolean;
}

/**
 * Subscribe to a background process for the lifetime of the component, and read
 * its latest status from the store. Subscribing increments a refcount that the
 * poller middleware uses to decide whether to run its interval — so polling
 * happens only while at least one interested component is mounted AND there is a
 * non-final process to watch.
 */
export function useBackgroundProcess(
  objectType: BackgroundProcessObjectType,
  objectId: string,
): UseBackgroundProcess {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!objectId) return;
    dispatch(subscribeProcess({ objectType, objectId }));
    return () => {
      dispatch(unsubscribeProcess({ objectType, objectId }));
    };
  }, [dispatch, objectType, objectId]);

  const process = useAppSelector((s) => selectProcess(s, objectType, objectId));

  return {
    process,
    status: process?.status,
    isRunning: isRunningStatus(process?.status),
    isFinal: isFinalStatus(process?.status),
  };
}
